import { Loader } from "semantic-ui-react";

import { useSelectedLocation } from "components";
import { createFormHelper } from "lib/Form";
import { DmsSystem, General, KeylabelKeylocker, LocationOverviewHeader, Service, Statuses, Tyres, Workorder } from "modules/LocationOverviewDashboard/components";
import { useUpdateLocationMutation } from "modules/LocationOverviewDashboard/hooks";
import "modules/LocationOverviewDashboard/LocationOverviewDashboard.scss";
import { areIpAddressesValid, parseIpAddresses } from "modules/LocationOverviewDashboard/utils";

import type { DealerLocation } from "models";
import type { LocationDetailFormPayload } from "modules/LocationOverviewDashboard/hooks";

const FormHelper = createFormHelper<LocationDetailFormPayload>();

const getDefaultFormValues = (location: DealerLocation): LocationDetailFormPayload => ({
  name: location.name,
  commercial_name: location.commercial_name,
  headline: location.headline,
  footerline: location.footerline,
  country: location.country,
  city: location.city,
  street: location.street,
  postal_code: location.postal_code,
  phone_nr: location.phone_nr,
  language_code: location.language_code,
  logo: location.logo,
  company_stamp: location.company_stamp,
  ip_address_enabled: location.ip_address_enabled,
  ip_addresses: location.ip_address_enabled ? (location.ip_addresses?.join("\n") ?? "") : "",
  is_employee_name_visible: location.is_employee_name_visible,
  is_car_note_enabled: location.is_car_note_enabled,
  is_customer_note_enabled: location.is_customer_note_enabled,
  redirect_to_new_web_version: location.redirect_to_new_web_version,
  key_label_type_id: location.key_label_type_id,
  key_label_layout_id: location.key_label_layout_id,
  keylocker_code: location.keylocker_code,
  dms_location_source: location.dms_location_source,
  can_add_intervention_elements: location.can_add_intervention_elements,
  default_customer_ok_for_dms_lease_intervention: location.default_customer_ok_for_dms_lease_intervention,
  planit_planning_replacement_vehicle: location.planit_planning_replacement_vehicle,
  xentry_sub_outlet_id: location.xentry_sub_outlet_id,
  auto_unpin_appointment_statuses: location.auto_unpin_appointment_statuses ?? [],
  unpin_past_appointments_older_than: location.unpin_past_appointments_older_than,
  unpin_future_appointments_further_than: location.unpin_future_appointments_further_than,
  unpin_appointments_on_is_billed: location.unpin_appointments_on_is_billed,
  unpin_appointments_on_car_out_of_shop: location.unpin_appointments_on_car_out_of_shop,
  enabled_appointment_note_type_ids: location.enabled_appointment_note_type_ids ?? [],
  all_season_a_threshold: location.all_season_a_threshold,
  all_season_n_threshold: location.all_season_n_threshold,
  summer_a_threshold: location.summer_a_threshold,
  summer_n_threshold: location.summer_n_threshold,
  winter_a_threshold: location.winter_a_threshold,
  winter_n_threshold: location.winter_n_threshold,
  truck_tyre_a_threshold: location.truck_tyre_a_threshold,
  truck_tyre_n_threshold: location.truck_tyre_n_threshold,
  is_tyre_status_always_visible_on_wo: location.is_tyre_status_always_visible_on_wo,
  statuses: location.statuses ?? [],
  is_assigned_wo_status_change_notification_enabled: location.is_assigned_wo_status_change_notification_enabled,
  web_clock_omgeving_id: location.web_clock_omgeving_id,
  planit_planning_establishment_id: location.planit_planning_establishment_id
});

const isFormInvalid = (values: LocationDetailFormPayload) => {
  const areRequiredFieldsValid = Boolean(values.name && values.commercial_name && values.country && values.language_code);
  const isKeylockerCodeValid = !values.keylocker_code || (values.keylocker_code >= 1000 && values.keylocker_code <= 9999);
  const areUnpinNumbersValid = values.unpin_past_appointments_older_than >= 0 && values.unpin_future_appointments_further_than >= 0;
  const isIpAddressValid = !values.ip_address_enabled || areIpAddressesValid(values.ip_addresses);
  const areThresholdsValid =
    values.all_season_n_threshold <= values.all_season_a_threshold &&
    values.summer_n_threshold <= values.summer_a_threshold &&
    values.winter_n_threshold <= values.winter_a_threshold &&
    values.truck_tyre_n_threshold <= values.truck_tyre_a_threshold;

  return !(areRequiredFieldsValid && isKeylockerCodeValid && areUnpinNumbersValid && isIpAddressValid && areThresholdsValid);
};

export const LocationOverviewDashboard = () => {
  const { selectedLocation } = useSelectedLocation();
  const { mutate: updateLocation, isPending: isUpdateLocationPending } = useUpdateLocationMutation();

  if (!selectedLocation) return <Loader active />;

  const onSubmit = (values: LocationDetailFormPayload) => {
    if (isFormInvalid(values)) return;

    updateLocation({
      ...selectedLocation,
      ...values,
      ip_addresses: parseIpAddresses(values.ip_addresses)
    });
  };

  return (
    <FormHelper.Form key={selectedLocation.id} onSubmit={onSubmit} defaultValues={getDefaultFormValues(selectedLocation)}>
      <div className="LocationOverviewDashboard">
        <LocationOverviewHeader isUpdateLocationPending={isUpdateLocationPending} />
        <General tabId="general" />
        <Workorder tabId="workorder" />
        <KeylabelKeylocker tabId="keylabelKeylocker" />
        <DmsSystem tabId="dmsSystem" />
        <Service tabId="service" />
        <Tyres tabId="tyres" />
        <Statuses tabId="statuses" />
      </div>
    </FormHelper.Form>
  );
};
